import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { DocOption } from "building-types/options/optionSet";
import type { OptionSetOverride } from "building-types/options/optionTemplateTypes";
import { SystemOptionSet } from "doc/options/SystemOptionSetEnum";
import SectionHeader from "lib/components/builders/communities/plans/SectionHeader";
import PlanLibraryNonBoolOptionSetModal from "lib/components/builders/plan-library/modal/PlanLibraryNonBoolOptionSetModal";
import PlanLibraryOptionModal from "lib/components/builders/plan-library/modal/PlanLibraryOptionModal";
import PlanLibraryOptionRuleModal from "lib/components/builders/plan-library/modal/PlanLibraryOptionRuleModal";
import { PlanLibraryBadge } from "lib/components/builders/plan-library/PlanLibraryBadge";
import { PlanLibraryOptionValue } from "lib/components/builders/plan-library/PlanLibraryOptionValue";
import { planLibraryActions } from "lib/components/builders/plan-library/planLibraryReducer";
import { PlanLibraryRuleBadge } from "lib/components/builders/plan-library/PlanLibraryRuleBadge";
import { usePlanLibraryDispatch, usePlanLibrarySelector } from "lib/components/builders/plan-library/planLibraryStore";
import { type PlanLibraryOptionSet } from "lib/components/builders/plan-library/planLibraryTypes";
import {
  getPlanLibraryImages,
  planLibraryGetInheritedAndCurrentPrice,
  planLibraryIsOptionOverridden,
  planLibraryIsOptionSetOverridden,
} from "lib/components/builders/plan-library/planLibraryUtils";
import PlanCard, { PlanCards } from "lib/components/builders/plan_cards/PlanCard";
import { generatedElevationUrl } from "lib/components/builders/PlanThumbnail";
import type { ReactNode } from "react";
import Circle from "ui/components/Circle";
import { SimpleDropDownMenu } from "ui/components/DropdownMenu";
import Link from "ui/components/Link";
import useAppContext from "ui/hooks/useAppContext";
import IconCheck from "ui/icons/IconCheck";
import IconLocked from "ui/icons/IconLocked";
import styles from "./PlanLibraryElevationsTab.module.scss";

interface ElevationCardProps {
  builder: ClientBuilder;

  option: DocOption;
  optionSetOverride: OptionSetOverride;
  inheritedOptionSet: PlanLibraryOptionSet;

  imageUrl: string;
  isDefault: boolean;
  isReadOnly: boolean;
  enableModal: () => void;
  setAsDefault: () => void;
  toggleDisabled: () => void;
  discardOverrides: () => void;
}

function ElevationCard({
  builder,

  option,
  optionSetOverride,
  inheritedOptionSet,

  imageUrl,
  isDefault,
  isReadOnly,

  enableModal,
  setAsDefault,
  toggleDisabled,
  discardOverrides,
}: ElevationCardProps) {
  const menuItems: ReactNode[] = [
    <Link
      key="edit_elevation"
      onClick={(e) => {
        e.stopPropagation();
        enableModal();
      }}
    >
      Edit Elevation
    </Link>,
  ];

  if (!isDefault) {
    menuItems.push(
      <Link
        key="set_as_default"
        onClick={(e) => {
          e.stopPropagation();
          setAsDefault();
        }}
      >
        Set Elevation as Default
      </Link>,
    );
  }

  const inheritedOption = inheritedOptionSet?.options?.find(({ id }) => id === option.id);
  const optionOverrides = optionSetOverride?.options?.[option.id];

  const { inheritPrice, currentPrice } = planLibraryGetInheritedAndCurrentPrice(
    builder,
    inheritedOptionSet,
    optionSetOverride,
    option,
  );

  return (
    <PlanCard
      imgUrl={imageUrl}
      selected={!option.disabled}
      onSelect={isReadOnly ? undefined : toggleDisabled}
      selectable={!isDefault}
      imageWidth={300}
    >
      <div className="plan-card-row">
        <div className="plan-card-cell plan-card-name">
          <PlanLibraryOptionValue
            field="Name"
            inheritedValue={inheritedOption?.name}
            isOverridden={optionOverrides?.name !== undefined}
          >
            <div>{option.name}</div>
          </PlanLibraryOptionValue>
          {isDefault && (
            <div className={styles.baseOptionBadgeWrapper}>
              <div className={styles.cardOptionSetBadgeWrapper} onClick={(e) => e.stopPropagation()}>
                <PlanLibraryBadge isBaseOption />
              </div>
            </div>
          )}
          {!isReadOnly && <SimpleDropDownMenu menuItems={menuItems} />}
        </div>
      </div>
      <div className="plan-card-row">
        <div className="plan-card-cell col-2">
          <PlanLibraryOptionValue
            field="Price"
            inheritedValue={inheritPrice}
            isOverridden={optionOverrides?.priceTbd !== undefined || optionOverrides?.price !== undefined}
          >
            {currentPrice}
          </PlanLibraryOptionValue>
        </div>
        <div className="plan-card-cell col-2">
          <div className={styles.cardOptionSetBadgeWrapper}>
            <PlanLibraryBadge
              isOverridden={planLibraryIsOptionOverridden(optionOverrides)}
              onClickBadge={discardOverrides}
            />

            <PlanLibraryRuleBadge optionSet={inheritedOptionSet} optionId={option.id} />
          </div>
        </div>
      </div>
      <div className="plan-card-row">
        <div className={`plan-card-cell center${isDefault ? " disabled" : ""}`}>
          {isDefault ? (
            <>
              <IconLocked variant="portal" style={{ marginRight: 9 }} color="stoneLight" size={18} />
              Base Elevation
            </>
          ) : (
            <>
              {option.disabled ? (
                "Select"
              ) : (
                <>
                  <Circle size={18} style={{ marginRight: 8 }} bg="sapphire">
                    <IconCheck color="white" size={12} />
                  </Circle>
                  Selected
                </>
              )}
            </>
          )}
        </div>
      </div>
    </PlanCard>
  );
}

export default function PlanLibraryElevationsTab() {
  const { setUserNotification } = useAppContext();
  const dispatch = usePlanLibraryDispatch();

  const {
    builder,
    planName,
    planImages,
    masterPlan,
    isReadOnly,
    modalOption,
    modalOptionSet,
    elevationOptionSet,
    elevationOverrides,
    inheritedOptionSet,
    editOptionRules,
    isUnitLot,
  } = usePlanLibrarySelector((state) => ({
    builder: state.builder,
    planName: state.planName,
    planImages: getPlanLibraryImages(state),
    masterPlan: state.plans.masterPlan,
    isReadOnly: state.isReadOnly,
    modalOption: state.modalOption,
    modalOptionSet: state.modalOptionSet,
    editOptionRules: state.editOptionRules,
    elevationOptionSet: state.elevationOptionSet,
    elevationOverrides: state.overrides[SystemOptionSet.ELEVATION_STYLE],
    inheritedOptionSet: state.inheritedOptionSets[SystemOptionSet.ELEVATION_STYLE],
    isUnitLot: state.plans.planType === "lot" && !!state.plans.lot.reportingUnitId,
  }));

  // A unit lot shows the building's elevations but cannot override them
  const elevationsReadOnly = isReadOnly || isUnitLot;
  const overrideAction = elevationsReadOnly ? "View" : "Manage";
  const subtitle = isUnitLot
    ? `Elevations for ${planName} are set on its building plan`
    : `${overrideAction} elevation overrides for ${planName}`;

  function renderTitle(optionSet: PlanLibraryOptionSet) {
    return (
      <div className={styles.elevationTitle}>
        <div>Elevations {optionSet.options.length}</div>
        <div className={styles.elevationBadgeWrapper}>
          <PlanLibraryBadge
            onClickBadge={() => {
              dispatch(planLibraryActions.enableOverrideModal({ optionSet }));
              dispatch(
                planLibraryActions.setOverridesToDiscard({
                  overridesToDiscard: elevationOverrides,
                  onHandleDiscarding: () => {
                    dispatch(planLibraryActions.discardOptionSetOverride({ optionSet }));
                    setUserNotification({
                      message: `Overrides discarded for ${optionSet.name}`,
                      messageType: "success",
                    });
                  },
                }),
              );
            }}
            isOverridden={planLibraryIsOptionSetOverridden(elevationOverrides, optionSet)}
          />
        </div>
      </div>
    );
  }

  // An empty state, because throwing here would loop the page through its error boundary
  if (!elevationOptionSet) {
    return <SectionHeader title="Elevations" subtitle={`No elevations are available for ${planName}`} hr />;
  }

  return (
    <>
      <SectionHeader title={renderTitle(elevationOptionSet)} subtitle={subtitle} hr />

      <PlanCards>
        {elevationOptionSet.options.map((option) => {
          const url = generatedElevationUrl(planImages, option.id);
          const optionOverrides = elevationOverrides?.options?.[option.id];

          return (
            <ElevationCard
              builder={builder}
              key={option.id}
              option={option}
              optionSetOverride={elevationOverrides}
              inheritedOptionSet={inheritedOptionSet}
              imageUrl={url ?? masterPlan?.thumbnailUrl ?? ""}
              isDefault={option.id === elevationOptionSet.defaultValue}
              isReadOnly={elevationsReadOnly}
              enableModal={() => {
                dispatch(planLibraryActions.enableOverrideModal({ optionSet: elevationOptionSet, option }));
              }}
              setAsDefault={() => {
                dispatch(
                  planLibraryActions.overrideOptionSet({
                    newOptionSet: { ...elevationOptionSet, defaultValue: option.id },
                    oldOptionSet: elevationOptionSet,
                  }),
                );
              }}
              toggleDisabled={() => {
                const options = elevationOptionSet.options.map((x) => {
                  if (x.id === option.id) {
                    return { ...x, disabled: !x.disabled };
                  } else {
                    return x;
                  }
                });
                dispatch(
                  planLibraryActions.overrideOptionSet({
                    newOptionSet: { ...elevationOptionSet, options },
                    oldOptionSet: elevationOptionSet,
                  }),
                );
              }}
              discardOverrides={() => {
                dispatch(planLibraryActions.enableOverrideModal({ optionSet: elevationOptionSet, option }));
                dispatch(
                  planLibraryActions.setOverridesToDiscard({
                    overridesToDiscard: { options: { [option.id]: optionOverrides ?? {} } },
                    onHandleDiscarding: () => {
                      dispatch(
                        planLibraryActions.discardOptionOverride({
                          optionSet: elevationOptionSet,
                          optionId: option.id,
                        }),
                      );
                      setUserNotification({
                        message: `Overrides discarded for ${option.name}`,
                        messageType: "success",
                      });
                    },
                  }),
                );
              }}
            />
          );
        })}

        {modalOptionSet && !modalOption && <PlanLibraryNonBoolOptionSetModal />}
        {modalOptionSet && modalOption && <PlanLibraryOptionModal />}
        {editOptionRules !== null && <PlanLibraryOptionRuleModal />}
      </PlanCards>
    </>
  );
}
